// «Cambios del sitio» — cola de aprobación y bitácora de la edición segura del
// sitio público (mcp_django apps/site_config/routers_edit.py, ya desplegado):
//
//   GET  /api/v1/admin/site-config/revisions?status=pending|applied|rejected|reverted
//   GET  /api/v1/admin/site-config/revisions/{id}         (diff + before/after)
//   POST /api/v1/admin/site-config/revisions/{id}/approve (solo humano admin)
//   POST /api/v1/admin/site-config/revisions/{id}/reject  (solo humano admin)
//   POST /api/v1/admin/site-config/revisions/{id}/revert  {reason, mode}
//
// approve/reject no están en el OpenAPI a propósito (el agente no puede
// aprobarse a sí mismo); el backend exige un login humano con rol admin/cto.

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Bot, Check, CheckCircle2, ExternalLink, History, RotateCcw, User, X } from 'lucide-react';

import { djangoApiCall } from '@/lib/api';
import { useModules } from '@/context/ModulesContext';
import { useEditPolicy, usePrimaryDomain, useRevisionDetail, useRevisions, type Revision } from '../data';
import { Button, Card, EmptyState, ErrorNote, Loading, PageHeader } from '../ui';

const FILTERS: Array<{ key: string | null; label: string }> = [
  { key: 'pending', label: 'Por aprobar' },
  { key: 'applied', label: 'Aplicados' },
  { key: 'rejected', label: 'Rechazados' },
  { key: 'reverted', label: 'Deshechos' },
  { key: null, label: 'Todos' },
];

const STATUS_LABEL: Record<string, string> = {
  pending: 'Por aprobar',
  applied: 'Aplicado',
  rejected: 'Rechazado',
  reverted: 'Deshecho',
};

const OP_LABEL: Record<string, string> = {
  update: 'Edición',
  create: 'Sección nueva',
  enable: 'Mostrar sección',
  disable: 'Ocultar sección',
  reorder: 'Cambio de orden',
  revert: 'Deshacer',
};

const fmt = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

function when(s?: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? '' : fmt.format(d);
}

function pageName(slug: string): string {
  return slug ? `/${slug}` : 'Inicio';
}

function show(v: unknown): string {
  if (v === null || v === undefined) return '(vacío)';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'pending'
      ? 'bg-primary/10 text-primary'
      : status === 'applied'
        ? 'bg-muted text-foreground'
        : 'bg-muted text-muted-foreground';
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>{STATUS_LABEL[status] ?? status}</span>;
}

function ActorChip({ kind, label }: { kind: string; label?: string }) {
  const agent = kind === 'agent' || kind === 'system';
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      {agent ? <Bot size={12} aria-hidden /> : <User size={12} aria-hidden />}
      {agent ? 'Propuesto por el agente' : `Por ${label || 'una persona'}`}
    </span>
  );
}

export function SiteChangesPage() {
  const { isAdmin } = useModules();
  const [filter, setFilter] = useState<string | null>('pending');
  const [selected, setSelected] = useState<number | null>(null);
  const list = useRevisions(filter, 100);
  const policy = useEditPolicy();
  const domain = usePrimaryDomain();

  // Al cambiar de filtro, selecciona el primero (escritorio) o nada.
  useEffect(() => {
    setSelected(null);
  }, [filter]);

  const rows = list.data ?? [];

  return (
    <div className="mx-auto w-full max-w-[1300px] px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        title="Cambios del sitio"
        subtitle={
          policy.data?.edit_mode === 'apply'
            ? 'El agente aplica cambios directo; aquí ves cada uno y puedes deshacerlo.'
            : 'El agente propone cambios a tu sitio y tú decides: apruébalos, recházalos o deshazlos después.'
        }
        actions={
          domain.data && (
            <a
              href={`https://${domain.data}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 py-2 text-sm font-medium text-foreground hover:bg-muted"
            >
              Ver sitio <ExternalLink size={14} aria-hidden />
              <span className="sr-only">(se abre en otra pestaña)</span>
            </a>
          )
        }
      />

      <div role="tablist" aria-label="Filtrar cambios" className="mb-4 flex gap-1 overflow-x-auto rounded-xl border border-border bg-card p-1">
        {FILTERS.map((f) => {
          const on = filter === f.key;
          return (
            <button
              key={f.label}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setFilter(f.key)}
              className={`shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                on ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <Card className="lg:col-span-5" title={`${FILTERS.find((f) => f.key === filter)?.label}${list.data ? ` · ${rows.length}` : ''}`} titleId="rev-list">
          {list.isLoading ? (
            <Loading />
          ) : list.isError ? (
            <ErrorNote>No se pudo leer la bitácora del sitio.</ErrorNote>
          ) : rows.length === 0 ? (
            <EmptyState icon={filter === 'pending' ? <CheckCircle2 size={18} /> : <History size={18} />} title={filter === 'pending' ? 'No hay cambios por aprobar' : 'Sin cambios en esta lista'}>
              {filter === 'pending'
                ? 'Cuando le pidas al asistente un cambio en tu sitio, la propuesta llegará aquí para que la revises antes de publicarse.'
                : 'Aquí aparecerá el historial de cambios de tu sitio.'}
            </EmptyState>
          ) : (
            <ul className="-mx-2 flex flex-col gap-1">
              {rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(r.id)}
                    aria-current={selected === r.id ? 'true' : undefined}
                    className={`w-full rounded-xl px-3 py-3 text-left transition-colors hover:bg-muted ${selected === r.id ? 'bg-muted' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{r.summary || `${OP_LABEL[r.op] ?? r.op} en ${r.section_key}`}</span>
                      <StatusPill status={r.status} />
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span>{pageName(r.page_slug)} · {r.section_key}</span>
                      <ActorChip kind={r.actor_kind} label={r.actor} />
                      <span>{when(r.created_at)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <div className="lg:col-span-7">
          {selected === null ? (
            <Card title="Detalle" titleId="rev-detail-empty">
              <EmptyState title="Elige un cambio de la lista">
                Verás qué decía antes, qué dirá después y quién lo propuso.
              </EmptyState>
            </Card>
          ) : (
            <RevisionDetailCard id={selected} isAdmin={isAdmin} onDone={() => setSelected(null)} />
          )}
        </div>
      </div>
    </div>
  );
}

function RevisionDetailCard({ id, isAdmin, onDone }: { id: number; isAdmin: boolean; onDone: () => void }) {
  const qc = useQueryClient();
  const detail = useRevisionDetail(id);
  const [confirm, setConfirm] = useState<null | 'approve' | 'reject' | 'revert'>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  useEffect(() => {
    setConfirm(null);
    setReason('');
    setErr(null);
    setOkMsg(null);
  }, [id]);

  const r = detail.data;

  const run = async (kind: 'approve' | 'reject' | 'revert') => {
    setBusy(true);
    setErr(null);
    try {
      const res = await djangoApiCall<{ summary?: string; note?: string; status?: string }>(
        `/admin/site-config/revisions/${id}/${kind}`,
        {
          method: 'POST',
          // Deshacer desde el panel lo decide una persona admin → se aplica ya.
          body: kind === 'revert' ? JSON.stringify({ reason: reason.trim(), mode: 'apply' }) : undefined,
        },
      );
      setOkMsg(res?.note || res?.summary || 'Listo.');
      setConfirm(null);
      await qc.invalidateQueries({ queryKey: ['orion-shell', 'revisions'] });
      await qc.invalidateQueries({ queryKey: ['orion-shell', 'revision', id] });
    } catch (e: any) {
      setErr(e?.message || 'No se pudo completar la acción.');
    } finally {
      setBusy(false);
    }
  };

  if (detail.isLoading) return <Card title="Detalle"><Loading /></Card>;
  if (detail.isError || !r) return <Card title="Detalle"><ErrorNote>No se pudo leer este cambio.</ErrorNote></Card>;

  return (
    <Card
      title={r.summary || `${OP_LABEL[r.op] ?? r.op} en ${r.section_key}`}
      titleId="rev-detail"
      action={
        <button type="button" onClick={onDone} aria-label="Cerrar detalle" className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted lg:hidden">
          <X size={16} />
        </button>
      }
    >
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <div><dt className="text-xs text-muted-foreground">Estado</dt><dd className="mt-0.5"><StatusPill status={r.status} /></dd></div>
        <div><dt className="text-xs text-muted-foreground">Dónde</dt><dd className="mt-0.5 text-foreground">{pageName(r.page_slug)} · {r.section_key}</dd></div>
        <div><dt className="text-xs text-muted-foreground">Quién</dt><dd className="mt-0.5"><ActorChip kind={r.actor_kind} label={r.actor} /></dd></div>
        <div><dt className="text-xs text-muted-foreground">Cuándo</dt><dd className="mt-0.5 text-foreground">{when(r.created_at)}</dd></div>
        {r.reason && (
          <div className="sm:col-span-2"><dt className="text-xs text-muted-foreground">Motivo</dt><dd className="mt-0.5 text-foreground">{r.reason}</dd></div>
        )}
        {r.applied_by && (
          <div className="sm:col-span-2">
            <dt className="text-xs text-muted-foreground">{r.status === 'rejected' ? 'Rechazado por' : 'Aplicado por'}</dt>
            <dd className="mt-0.5 text-foreground">{r.applied_by}{r.applied_at ? ` · ${when(r.applied_at)}` : ''}</dd>
          </div>
        )}
      </dl>

      <h3 className="mb-2 mt-5 text-sm font-semibold text-foreground">Qué cambia</h3>
      {r.diff?.length ? (
        <ul className="space-y-3">
          {r.diff.map((d, i) => (
            <li key={`${d.path}-${i}`} className="rounded-xl border border-border">
              <p className="border-b border-border px-3 py-1.5 font-mono text-[12px] text-muted-foreground">{d.path || '(sección completa)'}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2">
                <div className="border-b border-border p-3 sm:border-b-0 sm:border-r">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Antes</p>
                  <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words text-xs text-foreground line-through decoration-destructive/60">{show(d.before)}</pre>
                </div>
                <div className="p-3">
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-primary">Después</p>
                  <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words text-xs text-foreground">{show(d.after)}</pre>
                </div>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">Este cambio no trae diferencias de contenido.</p>
      )}

      {okMsg && <p className="mt-4 rounded-lg bg-primary/10 px-3 py-2 text-sm text-primary" role="status">{okMsg}</p>}
      {err && <div className="mt-4"><ErrorNote>{err}</ErrorNote></div>}

      {/* Acciones */}
      {!okMsg && r.status === 'pending' && isAdmin && (
        <div className="mt-5 border-t border-border pt-4">
          {confirm === null ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" onClick={() => setConfirm('approve')}><Check size={15} aria-hidden /> Aprobar y publicar</Button>
              <Button variant="danger" onClick={() => setConfirm('reject')}><X size={15} aria-hidden /> Rechazar</Button>
            </div>
          ) : (
            <div className="rounded-xl bg-muted p-3">
              <p className="text-sm text-foreground">
                {confirm === 'approve'
                  ? '¿Publicar este cambio en tu sitio? Lo podrás deshacer después.'
                  : '¿Rechazar la propuesta? Tu sitio no cambia.'}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant={confirm === 'approve' ? 'primary' : 'danger'} disabled={busy} onClick={() => void run(confirm as 'approve' | 'reject')}>
                  {busy ? 'Procesando…' : confirm === 'approve' ? 'Sí, publicar' : 'Sí, rechazar'}
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => setConfirm(null)}>Cancelar</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!okMsg && r.status === 'applied' && isAdmin && (
        <div className="mt-5 border-t border-border pt-4">
          {confirm !== 'revert' ? (
            <Button variant="secondary" onClick={() => setConfirm('revert')}><RotateCcw size={15} aria-hidden /> Deshacer este cambio</Button>
          ) : (
            <div className="rounded-xl bg-muted p-3">
              <label htmlFor="revert-reason" className="text-sm font-medium text-foreground">¿Por qué lo deshaces?</label>
              <textarea
                id="revert-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="Ej.: el texto anterior explicaba mejor el servicio"
                className="mt-2 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="primary" disabled={busy || !reason.trim()} onClick={() => void run('revert')}>
                  {busy ? 'Procesando…' : 'Deshacer ahora'}
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => setConfirm(null)}>Cancelar</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {!isAdmin && r.status === 'pending' && (
        <p className="mt-5 border-t border-border pt-4 text-sm text-muted-foreground">Solo una persona administradora puede aprobar o rechazar cambios del sitio.</p>
      )}
    </Card>
  );
}

export type { Revision };
export default SiteChangesPage;
